import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, Users, Activity, Shield, TrendingUp, Key, AlertCircle, Plus, Clock, BarChart3, Inbox, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardHome({ user }) {
  const [stats, setStats] = useState({
    totalCredentials: 0,
    sharedCredentials: 0,
    recentActivity: [],
    teamMembers: 0,
    myCredentials: 0,
    totalCertificates: 0
  });
  const [planUsage, setPlanUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [credsRes, certsRes, usersRes, planRes] = await Promise.all([
        axios.get(`${API}/credentials`, { headers }),
        axios.get(`${API}/certificates`, { headers }),
        axios.get(`${API}/users`, { headers }),
        axios.get(`${API}/plan-usage`, { headers })
      ]);

      const credentials = credsRes.data;
      
      let recentActivity = [];
      if (user?.role === 'admin') {
        try {
          const logsRes = await axios.get(`${API}/audit-logs`, { headers });
          recentActivity = logsRes.data.slice(0, 5);
        } catch (err) {
          // Audit logs may not be accessible for non-admin
        }
      }
      
      setStats({
        totalCredentials: credentials.length,
        sharedCredentials: credentials.filter(c => c.shared_with.length > 0).length,
        myCredentials: credentials.filter(c => c.created_by === user.id).length,
        teamMembers: usersRes.data.length,
        totalCertificates: certsRes.data.length,
        recentActivity
      });
      setPlanUsage(planRes.data);
    } catch (error) {
      toast.error('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-text-primary font-manrope text-xl">Loading dashboard...</div>
      </div>
    );
  }

  const statCards = [
    { title: 'Total Credentials', value: stats.totalCredentials, icon: Lock, color: 'bg-primary/10 text-primary', change: '+12% from last month' },
    { title: 'My Credentials', value: stats.myCredentials, icon: Key, color: 'bg-success/10 text-success', change: `${stats.myCredentials} owned by you` },
    { title: 'Shared Access', value: stats.sharedCredentials, icon: Users, color: 'bg-blue-100 text-blue-600', change: 'Across team members' },
    { title: 'Team Members', value: stats.teamMembers, icon: Shield, color: 'bg-purple-100 text-purple-600', change: `${stats.teamMembers} active users` },
    { title: 'Certificates', value: stats.totalCertificates, icon: Shield, color: 'bg-teal-100 text-teal-600', change: 'Managed certificates' }
  ];

  const quickActions = [
    { label: 'Add Credential', icon: Plus, action: () => navigate('/dashboard/add'), color: 'bg-primary text-white hover:bg-primary-hover' },
    { label: 'Add Certificate', icon: Shield, action: () => navigate('/dashboard/certificates/add'), color: 'bg-teal-600 text-white hover:bg-teal-700' },
    { label: 'Inbox', icon: Inbox, action: () => navigate('/dashboard/inbox'), color: 'bg-white border-2 border-gray-200 text-text-primary hover:border-black' },
    { label: 'Sent', icon: Send, action: () => navigate('/dashboard/sent'), color: 'bg-white border-2 border-gray-200 text-text-primary hover:border-black' },
    { label: 'View All', icon: Lock, action: () => navigate('/dashboard/credentials'), color: 'bg-white border-2 border-gray-200 text-text-primary hover:border-black' },
    { label: 'Team Members', icon: Users, action: () => navigate('/dashboard/users'), color: 'bg-white border-2 border-gray-200 text-text-primary hover:border-black', adminOnly: true },
    { label: 'Audit Logs', icon: Activity, action: () => navigate('/dashboard/audit-logs'), color: 'bg-white border-2 border-gray-200 text-text-primary hover:border-black', adminOnly: true }
  ];

  const getUsagePercent = (used, max) => {
    if (max === -1) return 0;
    return Math.min(100, Math.round((used / max) * 100));
  };

  const getBarColor = (percent) => {
    if (percent >= 90) return 'bg-red-500';
    if (percent >= 70) return 'bg-amber-500';
    return 'bg-primary';
  };

  const formatLimit = (val) => val === -1 ? 'Unlimited' : val;

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="font-manrope font-bold text-4xl text-text-primary tracking-tight mb-2">
          Dashboard Overview
        </h1>
        <p className="text-text-secondary">
          Welcome back, {user?.full_name} • {user?.role.toUpperCase()}
        </p>
      </div>

      {/* Statistics Cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        {statCards.map((stat) => (
          <div key={stat.title} className="bg-white border border-gray-200 p-6 hover:border-black transition-colors cursor-pointer" data-testid={`stat-card-${stat.title.toLowerCase().replace(/ /g, '-')}`}>
            <div className="flex items-start justify-between mb-4">
              <div className={`p-3 ${stat.color}`}><stat.icon className="w-6 h-6" strokeWidth={1.5} /></div>
            </div>
            <div>
              <p className="text-text-muted text-sm mb-1">{stat.title}</p>
              <p className="font-manrope font-bold text-3xl text-text-primary mb-2">{stat.value}</p>
              <p className="text-xs text-text-secondary">{stat.change}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Plan Usage Widget */}
      {planUsage && (
        <div className="bg-white border border-gray-200 p-6 mb-8" data-testid="plan-usage-widget">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <BarChart3 size={22} className="text-primary" />
              <h2 className="font-manrope font-semibold text-xl text-text-primary">Plan Usage</h2>
            </div>
            <span className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full bg-primary/10 text-primary" data-testid="plan-badge">
              {planUsage.plan}
            </span>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { label: 'Credentials', used: planUsage.usage.credentials, max: planUsage.limits.max_credentials, icon: Lock },
              { label: 'Certificates', used: planUsage.usage.certificates, max: planUsage.limits.max_certificates, icon: Shield },
              { label: 'Team Members', used: planUsage.usage.users, max: planUsage.limits.max_users, icon: Users }
            ].map((item) => {
              const pct = getUsagePercent(item.used, item.max);
              return (
                <div key={item.label} className="p-4 bg-gray-50 rounded-xl border border-gray-100" data-testid={`usage-${item.label.toLowerCase().replace(/ /g, '-')}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <item.icon size={16} className="text-text-muted" />
                      <span className="text-sm font-medium text-text-primary">{item.label}</span>
                    </div>
                    <span className="text-sm font-bold text-text-primary">{item.used} / {formatLimit(item.max)}</span>
                  </div>
                  {item.max !== -1 ? (
                    <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all duration-500 ${getBarColor(pct)}`} style={{ width: `${pct}%` }} />
                    </div>
                  ) : (
                    <div className="h-2.5 bg-primary/20 rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-primary/40 w-full" />
                    </div>
                  )}
                  <p className="text-xs text-text-muted mt-2">{item.max === -1 ? 'Unlimited' : `${pct}% used`}</p>
                </div>
              );
            })}
          </div>

          {/* Feature Access */}
          <div className="mt-5 pt-4 border-t border-gray-100">
            <p className="text-xs text-text-muted uppercase tracking-wider mb-3 font-medium">Feature Access</p>
            <div className="flex flex-wrap gap-3">
              {[
                { label: 'File Attachments', enabled: planUsage.limits.file_attachments },
                { label: 'Export to Excel', enabled: planUsage.limits.export },
                { label: 'Audit Logs', enabled: planUsage.limits.audit_logs }
              ].map((feat) => (
                <span key={feat.label} className={`text-xs px-3 py-1.5 rounded-full font-medium ${feat.enabled ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-gray-100 text-gray-400 border border-gray-200 line-through'}`} data-testid={`feature-${feat.label.toLowerCase().replace(/ /g, '-')}`}>
                  {feat.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="mb-8">
        <h2 className="font-manrope font-semibold text-xl text-text-primary mb-4">Quick Actions</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickActions
            .filter(action => !action.adminOnly || user?.role === 'admin')
            .map((action) => (
              <button key={action.label} onClick={action.action} className={`p-4 flex items-center gap-3 font-medium transition-all ${action.color}`} data-testid={`quick-action-${action.label.toLowerCase().replace(/ /g, '-')}`}>
                <action.icon size={20} strokeWidth={1.5} />
                {action.label}
              </button>
            ))}
        </div>
      </div>

      {/* Recent Activity - Admin Only */}
      {user?.role === 'admin' && stats.recentActivity.length > 0 && (
        <div className="bg-white border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Clock size={20} className="text-primary" />
            <h2 className="font-manrope font-semibold text-xl text-text-primary">Recent Activity</h2>
          </div>
          <div className="space-y-3">
            {stats.recentActivity.map((log) => (
              <div key={log.id || `${log.timestamp}-${log.action}`} className="flex items-start gap-3 p-3 bg-gray-50 border border-gray-100" data-testid={`activity-${log.id || log.action}`}>
                <Activity size={16} className="text-text-muted mt-1" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-text-primary">{log.action.replace(/_/g, ' ').toUpperCase()}</p>
                  <p className="text-xs text-text-secondary">{log.user_name} ({log.user_email})</p>
                  {log.resource_title && <p className="text-xs text-text-muted mt-1">Resource: {log.resource_title}</p>}
                </div>
                <span className="text-xs text-text-muted whitespace-nowrap">{new Date(log.timestamp).toLocaleTimeString()}</span>
              </div>
            ))}
          </div>
          <button onClick={() => navigate('/dashboard/audit-logs')} className="mt-4 text-primary hover:underline text-sm font-medium" data-testid="btn-view-all-logs">View All Logs →</button>
        </div>
      )}

      {/* Security Tips */}
      <div className="mt-8 bg-primary/5 border border-primary/20 p-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="text-primary w-5 h-5 mt-0.5" />
          <div>
            <h3 className="font-manrope font-semibold text-text-primary mb-2">Security Best Practices</h3>
            <ul className="text-sm text-text-secondary space-y-1">
              <li>• Use strong, unique passwords for each credential</li>
              <li>• Regularly rotate sensitive credentials</li>
              <li>• Only share credentials with team members who need access</li>
              <li>• Review audit logs periodically for unusual activity</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}